/**
 * MemberInfoTab (admin user page): the member record linked to this login.
 *
 *  - No member yet: create one through the SDK (POST api/members with this userId), or link an
 *    existing matching record (POST api/members/{id}/link-user) found by check-duplicate.
 *  - Member exists: edit the main fields (partial PUT api/members/{id}; only changed fields are
 *    sent, a cleared field is sent as null) and open the full member profile.
 *  - Another record matches: merge it into this one (POST api/members/{id}/merge/{otherId}), which
 *    moves addresses, health, households, campus, notes and emergency contacts and lists the other
 *    modules to check, or soft-delete it.
 *
 * Select values are strings throughout; gender is Male or Female.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconDeviceFloppy, IconRestore, IconAlertCircle, IconUserPlus, IconLink, IconTrash, IconGitMerge, IconExternalLink, IconCheck,
} from '@tabler/icons-react';
import {
  Stack, Card, Group, Button, TextInput, Select, Switch, SimpleGrid, Text, Alert, Loader, Center, Badge, Modal, Checkbox, DatePickerInput,
} from '../../../../components/ui';
import { toast } from '../../../../components/ui/providers/AlertProvider';
import { useMemberService, type Member, type MemberMergeResult, type UpdateMemberRequest } from '@actschurch/ccms-sdk';
import {
  GENDER_OPTIONS, dobDateOnly, dobToDate, dateToDob, textOrNull, errorMessage, errorStatus, memberFullName, useMaritalStatuses, useMemberTypes,
} from '../../../../components/members';

interface MemberInfoTabProps {
  userId: string;
  userEmail?: string;
  userFirstName?: string;
  userSurname?: string;
  userCellphone?: string;
  onUpdate?: () => void;
}

interface Form {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string | null;
  maritalStatusID: string | null;
  email: string;
  cellNumber: string;
  altContact: string;
  telephone: string;
  isMember: boolean;
  isActive: boolean;
  typeID: string | null;
  memberCode: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normaliseGender(value?: string | null): string | null {
  const v = (value ?? '').trim().toLowerCase();
  if (v === 'm' || v === 'male') return 'Male';
  if (v === 'f' || v === 'female') return 'Female';
  return null;
}

function formFromMember(m: Member): Form {
  return {
    firstName: m.firstName ?? '',
    middleName: m.middleName ?? '',
    lastName: m.lastName ?? '',
    dateOfBirth: dobToDate(m.dateOfBirth),
    gender: normaliseGender(m.gender),
    maritalStatusID: m.maritalStatusID > 0 ? String(m.maritalStatusID) : null,
    email: m.email ?? '',
    cellNumber: m.cellNumber ?? '',
    altContact: m.altContact ?? '',
    telephone: m.telephone ?? '',
    isMember: m.isMember,
    isActive: m.isActive,
    typeID: m.typeID > 0 ? String(m.typeID) : null,
    memberCode: m.memberCode ?? '',
  };
}

export function MemberInfoTab({ userId, userEmail, userFirstName, userSurname, userCellphone, onUpdate }: MemberInfoTabProps) {
  const navigate = useNavigate();
  const memberService = useMemberService();
  const { options: typeOptions } = useMemberTypes();
  const { options: maritalOptions } = useMaritalStatuses();

  const fromUser = useMemo<Form>(() => ({
    firstName: userFirstName ?? '', middleName: '', lastName: userSurname ?? '', dateOfBirth: null, gender: null, maritalStatusID: null,
    email: userEmail ?? '', cellNumber: userCellphone ?? '', altContact: '', telephone: '', isMember: true, isActive: true, typeID: null, memberCode: '',
  }), [userFirstName, userSurname, userEmail, userCellphone]);

  const [member, setMember] = useState<Member | null>(null);
  const [duplicate, setDuplicate] = useState<Member | null>(null);
  const [form, setForm] = useState<Form>(fromUser);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeUnderstood, setMergeUnderstood] = useState(false);
  const [mergeResult, setMergeResult] = useState<MemberMergeResult | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let current: Member | null = null;
      try {
        current = await memberService.getByUserId(userId);
      } catch (e) {
        if (errorStatus(e) !== 404 && !/not found/i.test(errorMessage(e, ''))) throw e;
      }
      setMember(current);
      setForm(current ? formFromMember(current) : fromUser);

      // Look for another record that matches this person (same names + same email or cellphone).
      const first = current?.firstName || userFirstName;
      const last = current?.lastName || userSurname;
      const email = current?.email || userEmail;
      const cell = current?.cellNumber || userCellphone;
      let dup: Member | null = null;
      if (first && last && (email || cell)) {
        dup = await memberService.checkDuplicate(first, last, email || null, cell || null).catch(() => null);
      }
      setDuplicate(dup && !dup.isDeleted && dup.memberID !== current?.memberID ? dup : null);
    } catch (e) {
      setLoadError(errorMessage(e, 'Could not load the member record.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const changes = useMemo<UpdateMemberRequest>(() => {
    const c: UpdateMemberRequest = {};
    if (!member) return c;
    const base = formFromMember(member);
    if (form.firstName.trim() !== base.firstName.trim()) c.firstName = form.firstName.trim();
    if (form.middleName.trim() !== base.middleName.trim()) c.middleName = textOrNull(form.middleName);
    if (form.lastName.trim() !== base.lastName.trim()) c.lastName = textOrNull(form.lastName);
    const dob = dateToDob(form.dateOfBirth);
    if ((dob ?? '') !== (dobDateOnly(member.dateOfBirth) ?? '')) c.dateOfBirth = dob;
    if (form.gender !== base.gender) c.gender = form.gender;
    if (form.maritalStatusID !== base.maritalStatusID) c.maritalStatusID = form.maritalStatusID ? Number(form.maritalStatusID) : null;
    // Email: "" clears it (null means "unchanged" to the API).
    if (form.email.trim() !== base.email.trim()) c.email = form.email.trim();
    if (form.cellNumber.trim() !== base.cellNumber.trim()) c.cellNumber = textOrNull(form.cellNumber);
    if (form.altContact.trim() !== base.altContact.trim()) c.altContact = textOrNull(form.altContact);
    if (form.telephone.trim() !== base.telephone.trim()) c.telephone = textOrNull(form.telephone);
    if (form.isMember !== base.isMember) c.isMember = form.isMember;
    if (form.isActive !== base.isActive) c.isActive = form.isActive;
    if (form.typeID !== base.typeID) c.typeID = form.typeID ? Number(form.typeID) : null;
    if (form.memberCode.trim() !== base.memberCode.trim()) c.memberCode = textOrNull(form.memberCode);
    return c;
  }, [form, member]);

  const isCreate = !member;
  const dirty = isCreate || Object.keys(changes).length > 0;
  const emailError = form.email.trim() && !EMAIL_RE.test(form.email.trim()) ? 'Enter a valid email address' : null;
  const firstNameError = !form.firstName.trim() ? 'First name is required' : null;

  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    setSaveError(null);
    try { await fn(); } catch (e) { setSaveError(errorMessage(e)); } finally { setSaving(false); }
  };

  const save = () => run(async () => {
    if (isCreate) {
      const body: Member = {
        memberID: 0,
        userId,
        firstName: form.firstName.trim(),
        middleName: textOrNull(form.middleName),
        lastName: textOrNull(form.lastName),
        dateOfBirth: dateToDob(form.dateOfBirth),
        gender: form.gender,
        maritalStatusID: form.maritalStatusID ? Number(form.maritalStatusID) : 0,
        email: form.email.trim(),
        cellNumber: textOrNull(form.cellNumber),
        altContact: textOrNull(form.altContact),
        telephone: textOrNull(form.telephone),
        isMember: form.isMember,
        isActive: form.isActive,
        avatar: null,
        typeID: form.typeID ? Number(form.typeID) : 0,
        memberCode: textOrNull(form.memberCode),
      };
      try {
        await memberService.create(body);
      } catch (e) {
        toast.error('Could not create the member profile', errorMessage(e));
        throw e;
      }
      toast.success('Member profile created');
    } else {
      try {
        await memberService.update(member.memberID, changes);
      } catch (e) {
        toast.error('Could not save', errorMessage(e));
        throw e;
      }
      toast.success('Saved', 'Member details updated.');
    }
    await load();
    onUpdate?.();
  });

  const linkExisting = () => run(async () => {
    if (!duplicate) return;
    try {
      await memberService.linkUser(duplicate.memberID, userId);
    } catch (e) {
      toast.error('Could not link the record', errorMessage(e));
      throw e;
    }
    toast.success('Record linked', `${memberFullName(duplicate)} is now linked to this login.`);
    await load();
    onUpdate?.();
  });

  const merge = () => run(async () => {
    if (!member || !duplicate) return;
    let result: MemberMergeResult;
    try {
      result = await memberService.merge(member.memberID, duplicate.memberID);
    } catch (e) {
      toast.error('Could not merge', errorMessage(e));
      throw e;
    }
    toast.success('Records merged');
    setMergeOpen(false);
    setMergeUnderstood(false);
    setMergeResult(result);
    await load();
    onUpdate?.();
  });

  const deleteDuplicate = () => run(async () => {
    if (!duplicate) return;
    try {
      await memberService.softDelete(duplicate.memberID);
    } catch (e) {
      toast.error('Could not delete the duplicate', errorMessage(e));
      throw e;
    }
    toast.success('Duplicate deleted', 'It can be restored from its member profile.');
    setDeleteOpen(false);
    await load();
  });

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md"><Loader /><Text size="sm" c="dimmed">Loading member information</Text></Stack>
      </Center>
    );
  }

  if (loadError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the member record">
        <Stack gap="xs"><Text size="sm">{loadError}</Text><Group><Button size="xs" variant="light" onClick={load}>Try again</Button></Group></Stack>
      </Alert>
    );
  }

  const dupName = duplicate ? memberFullName(duplicate) || `Member ${duplicate.memberID}` : '';
  const dupLinkedElsewhere = !!duplicate?.userId && duplicate.userId !== userId;

  return (
    <Stack gap="lg">
      {/* Linked member summary */}
      {member && (
        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" wrap="wrap">
            <Group gap="xs">
              <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Member #{member.memberID}</Badge>
              {member.memberCode && <Badge variant="light" color="gray">{member.memberCode}</Badge>}
              <Text size="sm" c="dimmed">Linked to this login</Text>
            </Group>
            <Button variant="light" size="sm" leftSection={<IconExternalLink size={16} />} onClick={() => navigate(`/admin/members/${member.memberID}`)}>
              View member profile
            </Button>
          </Group>
        </Card>
      )}

      {/* No member yet: link a matching record or create one */}
      {isCreate && duplicate && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="A matching member record exists">
          <Stack gap="sm">
            <Text size="sm">
              <b>{dupName}</b> (#{duplicate.memberID}{duplicate.email ? `, ${duplicate.email}` : ''}) has the same name and contact details.
              {dupLinkedElsewhere ? ' It is linked to a different login, so it cannot be linked here.' : ' Link it to this login instead of creating a second record.'}
            </Text>
            <Group gap="xs">
              {!dupLinkedElsewhere && (
                <Button size="xs" variant="light" leftSection={<IconLink size={14} />} loading={saving} onClick={linkExisting}>Link this record</Button>
              )}
              <Button size="xs" variant="subtle" leftSection={<IconExternalLink size={14} />} onClick={() => navigate(`/admin/members/${duplicate.memberID}`)}>Open it</Button>
            </Group>
          </Stack>
        </Alert>
      )}
      {isCreate && !duplicate && (
        <Alert color="blue" icon={<IconUserPlus size={16} />} title="No member profile yet">
          This login has no member record. Fill in the details below to create one.
        </Alert>
      )}

      {/* Member exists and another record matches */}
      {!isCreate && duplicate && (
        <Card withBorder padding="md" radius="md" style={{ borderColor: 'var(--mantine-color-orange-4)' }}>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600} c="orange">Possible duplicate record</Text>
              <Badge color="orange" variant="light">Duplicate</Badge>
            </Group>
            <Text size="sm">
              <b>{dupName}</b> (#{duplicate.memberID}{duplicate.email ? `, ${duplicate.email}` : ''}) matches this person.
              {dupLinkedElsewhere ? ' It is linked to another login: unlink it on its profile before merging.' : ''}
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="light" leftSection={<IconGitMerge size={14} />} disabled={dupLinkedElsewhere} onClick={() => { setMergeUnderstood(false); setMergeOpen(true); }}>
                Merge into this record
              </Button>
              <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />} disabled={!!duplicate.userId} onClick={() => setDeleteOpen(true)}>
                Delete duplicate
              </Button>
              <Button size="xs" variant="subtle" leftSection={<IconExternalLink size={14} />} onClick={() => navigate(`/admin/members/${duplicate.memberID}`)}>Open it</Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Form */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Text size="lg" fw={600}>Personal information</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput label="First name" required value={form.firstName} error={firstNameError} onChange={(e) => setForm({ ...form, firstName: e.currentTarget.value })} />
            <TextInput label="Middle name" value={form.middleName} onChange={(e) => setForm({ ...form, middleName: e.currentTarget.value })} />
            <TextInput label="Surname" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.currentTarget.value })} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <DatePickerInput label="Date of birth" clearable maxDate={new Date()} valueFormat="D MMMM YYYY" value={form.dateOfBirth}
              onChange={(v) => setForm({ ...form, dateOfBirth: (v as Date | null) ?? null })} />
            <Select label="Gender" placeholder="Choose" data={GENDER_OPTIONS} clearable value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} />
            <Select label="Marital status" placeholder="Choose" data={maritalOptions} clearable value={form.maritalStatusID} onChange={(v) => setForm({ ...form, maritalStatusID: v })} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Text size="lg" fw={600}>Contact information</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Email" value={form.email} error={emailError} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
            <TextInput label="Cellphone number" value={form.cellNumber} onChange={(e) => setForm({ ...form, cellNumber: e.currentTarget.value })} />
            <TextInput label="Alternative contact" value={form.altContact} onChange={(e) => setForm({ ...form, altContact: e.currentTarget.value })} />
            <TextInput label="Telephone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.currentTarget.value })} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Text size="lg" fw={600}>Membership</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Member type" placeholder="Choose" data={typeOptions} clearable value={form.typeID} onChange={(v) => setForm({ ...form, typeID: v })} />
            <TextInput label="Member code" value={form.memberCode} onChange={(e) => setForm({ ...form, memberCode: e.currentTarget.value })} />
            <Switch label="Church member" description="Off for visitors." checked={form.isMember} onChange={(e) => setForm({ ...form, isMember: e.currentTarget.checked })} />
            <Switch label="Active" description="Inactive people are left out of Care, Courses, Tasks and notifications." checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })} />
          </SimpleGrid>
        </Stack>
      </Card>

      {saveError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not saved">{saveError}</Alert>}

      {dirty && (
        <Group justify="flex-end">
          {!isCreate && (
            <Button variant="default" leftSection={<IconRestore size={16} />} onClick={() => { setForm(formFromMember(member)); setSaveError(null); }}>
              Discard changes
            </Button>
          )}
          <Button leftSection={isCreate ? <IconUserPlus size={16} /> : <IconDeviceFloppy size={16} />} loading={saving}
            disabled={!!firstNameError || !!emailError} onClick={save}>
            {isCreate ? 'Create member profile' : 'Save changes'}
          </Button>
        </Group>
      )}

      {/* Merge confirm */}
      <Modal opened={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge the duplicate into this record" centered size="md">
        <Stack gap="md">
          <Text size="sm">
            #{duplicate?.memberID} ({dupName}) is folded into #{member?.memberID}. Empty fields here are filled from it; its addresses, health,
            households, campus, notes and emergency contacts move across; then it is deleted. To compare the two field by field, use the Admin tab on the member profile.
          </Text>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            Other modules (Care, Courses, Departments, Forms, Locations, Projects, Carpool, Anatomy) are not updated. You get a checklist afterwards.
          </Alert>
          <Checkbox label="I understand" checked={mergeUnderstood} onChange={(e) => setMergeUnderstood(e.currentTarget.checked)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button color="red" leftSection={<IconGitMerge size={16} />} disabled={!mergeUnderstood} loading={saving} onClick={merge}>Merge records</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Merge result */}
      <Modal opened={!!mergeResult} onClose={() => setMergeResult(null)} title="Merge complete" centered size="md">
        {mergeResult && (
          <Stack gap="sm">
            <Text size="sm">
              {mergeResult.fieldsFilled.length > 0 ? `Filled: ${mergeResult.fieldsFilled.join(', ')}.` : 'No fields needed filling.'} Moved {mergeResult.moved.addresses} addresses,
              {' '}{mergeResult.moved.allergies + mergeResult.moved.disabilities} health items, {mergeResult.moved.families} households, {mergeResult.moved.notes} notes
              {' '}and {mergeResult.moved.emergencyContacts} emergency contacts.
            </Text>
            <Text size="sm" fw={600}>Check these modules for member #{mergeResult.mergedMemberID}:</Text>
            <Stack gap={4}>
              {mergeResult.modulesToCheck.map((m) => <Text key={m.module} size="sm">{m.module}: {m.what}</Text>)}
            </Stack>
            <Group justify="flex-end"><Button onClick={() => setMergeResult(null)}>Done</Button></Group>
          </Stack>
        )}
      </Modal>

      {/* Delete duplicate confirm */}
      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete the duplicate record?" centered size="md">
        <Stack gap="md">
          <Text size="sm">#{duplicate?.memberID} ({dupName}) is hidden from the directory and search. Nothing is erased and it can be restored from its profile. Nothing moves across: merge instead if it holds details worth keeping.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="red" leftSection={<IconTrash size={16} />} loading={saving} onClick={deleteDuplicate}>Delete duplicate</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
